import React, { useState, useEffect } from 'react';
import { Slider } from 'src/shared/ui/slider';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import { CancelRounded } from '@mui/icons-material';
import { usePlayerContext } from '../../context/player-context';
import { formatSeconds, parseTimeString, validateTimeInRange } from '../../utils/time-utils';

export const LoopModal: React.FC = () => {
  const {
    loopStart,
    loopEnd,
    setLoopStart,
    setLoopEnd,
    isLoopModalOpen,
    closeLoopModal,
    duration,
    handleSeek,
  } = usePlayerContext();
  const [localLoopStart, setLocalLoopStart] = useState(loopStart || 0);
  const [localLoopEnd, setLocalLoopEnd] = useState(loopEnd || duration);
  const [startInput, setStartInput] = useState(formatSeconds(loopStart || 0));
  const [endInput, setEndInput] = useState(formatSeconds(loopEnd || duration));
  const [startError, setStartError] = useState('');
  const [endError, setEndError] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  // Update local state when props change
  useEffect(() => {
    setLocalLoopEnd(loopEnd || duration);
    setEndInput(formatSeconds(loopEnd || duration));
  }, [duration, loopEnd]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsMobile(window.innerWidth < 900);

      const handleResize = () => setIsMobile(window.innerWidth < 900);
      window.addEventListener('resize', handleResize);

      return () => window.removeEventListener('resize', handleResize);
    }
    // If window is undefined (SSR), do nothing
    return undefined;
  }, []);

  const handleApply = () => {
    const parsedStart = parseTimeString(startInput);
    const parsedEnd = parseTimeString(endInput);

    let valid = true;

    if (parsedStart === null) {
      setStartError('Formato non valido. Usa HH:MM:SS.');
      valid = false;
    } else if (!validateTimeInRange(parsedStart, 0, duration)) {
      setStartError("Tempo di inizio fuori dall'intervallo.");
      valid = false;
    } else {
      setStartError('');
    }

    if (parsedEnd === null) {
      setEndError('Formato non valido. Usa HH:MM:SS.');
      valid = false;
    } else if (!validateTimeInRange(parsedEnd, 0, duration)) {
      setEndError("Tempo di fine fuori dall'intervallo.");
      valid = false;
    } else {
      setEndError('');
    }

    if (parsedStart !== null && parsedEnd !== null && parsedStart >= parsedEnd) {
      setStartError('Tempo di inizio deve essere inferiore al tempo di fine.');
      setEndError('Tempo di fine deve essere superiore al tempo di inizio.');
      valid = false;
    }

    if (!valid) return;

    setLoopStart(parsedStart);
    setLoopEnd(parsedEnd);
    handleSeek(parsedStart || 0, false);
    closeLoopModal();
  };

  const handleStartInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartInput(e.target.value);
    const parsed = parseTimeString(e.target.value);
    if (parsed !== null) {
      setLocalLoopStart(parsed);
      setStartError('');
    } else {
      setStartError('Formato non valido. Usa HH:MM:SS.');
    }
  };

  const handleEndInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndInput(e.target.value);
    const parsed = parseTimeString(e.target.value);
    if (parsed !== null) {
      setLocalLoopEnd(parsed);
      setEndError('');
    } else {
      setEndError('Formato non valido. Usa HH:MM:SS.');
    }
  };

  if (!isLoopModalOpen) {
    return null;
  }

  return (
    <Dialog
      open
      onClose={closeLoopModal}
      maxWidth="xs"
      fullWidth
      className="z-[1000]"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <DialogTitle className="flex items-center justify-between p-6">
        Imposta Punti di Loop
        <IconButton aria-label="close" onClick={closeLoopModal}>
          <CancelRounded className="text-action-divider" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="my-4">
          <div className="flex justify-between mb-2">
            <Typography variant="s2">Tempo inizio</Typography>
            <Typography variant="s2">Tempo fine</Typography>
          </div>
          <Slider
            min={0}
            max={duration}
            step={0.1}
            value={[localLoopStart, localLoopEnd]}
            onValueChange={(values) => {
              setLocalLoopStart(values[0]);
              setLocalLoopEnd(values[1]);
              setStartInput(formatSeconds(values[0]));
              setEndInput(formatSeconds(values[1]));
            }}
            className="w-full h-3"
          />
          <div className="flex justify-between mt-6">
            <TextField
              value={startInput}
              onChange={handleStartInputChange}
              error={!!startError}
              helperText={startError}
              placeholder="HH:MM:SS"
              size="medium"
            />

            <TextField
              value={endInput}
              onChange={handleEndInputChange}
              error={!!endError}
              helperText={endError}
              placeholder="HH:MM:SS"
              size="medium"
            />
          </div>
        </div>
      </DialogContent>

      <DialogActions
        className={`flex ${isMobile ? 'justify-start gap-2' : 'justify-between p-4'}`}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {isMobile && (
          <Button color="secondary" variant="outlined" size="small" onClick={closeLoopModal}>
            <Typography variant="button-s">Annulla</Typography>
          </Button>
        )}
        <Button color="primary" variant="contained" size="small" onClick={handleApply}>
          <Typography variant="button-s">Applica</Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};
